import { useState } from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router"
import { SUPPORT_EMAIL } from "@shared/contact"
import { NewsletterForm } from "./newsletter-form"

const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/sponsor", label: "Sponsor" },
  { to: "/nominate", label: "Nominate" },
  { to: "/partner-schools", label: "Partner Schools" },
  { to: "/events", label: "Events" },
  { to: "/contact", label: "Contact" },
] as const

export function ArrowUpRight({ className = "h-[14px] w-[14px]" }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" className={className} aria-hidden>
      <path d="M13.0964 0.359891L0.353516 13.1035" />
      <path d="M12.3456 11.6295C11.7888 11.0727 11.5076 9.69077 11.3667 8.43489C11.1839 6.81847 11.2541 5.17656 11.6366 3.59414C11.9227 2.4091 12.3846 1.08452 13.1028 0.366266" />
      <path d="M1.82685 1.11143C2.3836 1.66747 3.76556 1.94868 5.02143 2.08964C6.63785 2.27239 8.27977 2.20227 9.86218 1.81977C11.0472 1.5336 12.3718 1.07177 13.0901 0.353516" />
    </svg>
  )
}

function PublicHeader() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const navLinks = NAV_LINKS.map((link) =>
    (pathname === "/donor-signin" || pathname === "/sponsor") && link.to === "/partner-schools" ? { ...link, label: "Schools" } : link,
  )

  return (
    <header className="relative z-20 bg-cream-50 pt-5 lg:pt-0">
      <div className="public-container relative flex h-[38px] items-center justify-between gap-6 lg:h-[100px] lg:py-[25px]">
        <Link to="/" className="flex items-center gap-2 min-[1440px]:absolute min-[1440px]:left-[50px] min-[1440px]:top-[25px] min-[1440px]:h-[50px] min-[1440px]:w-[209px]" onClick={() => setOpen(false)}>
          <span className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-forest-950 min-[1440px]:h-10 min-[1440px]:w-10">
            <img src="/images/home/logo.png" alt="" className="h-full w-full object-contain p-1 min-[1440px]:p-1.5" />
          </span>
          <span className="font-display text-[22px] font-bold leading-[33px] tracking-tight text-ink min-[1440px]:text-[32px]">Redempta</span>
        </Link>

        <nav className="hidden items-center lg:flex lg:gap-3 lg:text-sm min-[1440px]:absolute min-[1440px]:left-[369px] min-[1440px]:top-[25px] min-[1440px]:h-[50px] min-[1440px]:gap-[21px] min-[1440px]:text-base" aria-label="Main">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `transition-colors duration-200 ${isActive ? "font-semibold text-forest-700" : "text-ink hover:text-forest-700"}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 min-[1440px]:absolute min-[1440px]:left-[1078px] min-[1440px]:top-[25px] min-[1440px]:h-[50px] min-[1440px]:w-[312px] min-[1440px]:justify-between">
          <Link to="/donor-signin" className="hidden text-base text-ink transition-colors hover:text-forest-700 min-[1440px]:inline-flex">
            Donor sign in
          </Link>
          <Link to="/sponsor" className="hidden items-center gap-[6px] rounded-full bg-forest-950 py-[6px] pl-[14px] pr-[5px] text-xs font-semibold text-white transition-colors hover:bg-forest-800 lg:inline-flex lg:gap-2 lg:py-1 lg:pl-4 lg:pr-1 lg:text-sm min-[1440px]:gap-3 min-[1440px]:py-1.5 min-[1440px]:pl-5 min-[1440px]:pr-1.5 min-[1440px]:text-base">
            <span className="min-[1440px]:hidden">Sponsor</span>
            <span className="hidden min-[1440px]:inline">Sponsor a Child</span>
            <span className="grid h-[26px] w-[26px] place-items-center rounded-full bg-white text-forest-950 min-[1440px]:h-10 min-[1440px]:w-10">
              <ArrowUpRight />
            </span>
          </Link>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full border border-forest-950/15 text-ink lg:hidden"
            aria-expanded={open}
            aria-label="Toggle navigation"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="relative block h-5 w-5" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className={`absolute inset-0 h-5 w-5 transition-opacity duration-200 motion-reduce:transition-none ${open ? "opacity-0" : "opacity-100"}`}
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className={`absolute inset-0 h-5 w-5 transition-[opacity,transform] duration-200 motion-reduce:transition-none ${open ? "rotate-90 opacity-100" : "rotate-0 opacity-0"}`}
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <nav
        aria-label="Mobile"
        aria-hidden={!open}
        inert={!open}
        className={`grid border-t bg-cream-50 transition-[grid-template-rows,opacity,border-color] duration-300 ease-out motion-reduce:transition-none lg:hidden ${
          open ? "grid-rows-[1fr] border-cream-200 opacity-100" : "grid-rows-[0fr] border-transparent opacity-0"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <ul className="flex flex-col gap-3 px-6 py-4">
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    isActive
                      ? "text-lg font-semibold text-forest-700"
                      : "text-lg text-ink transition-colors hover:text-forest-700"
                  }
                  onClick={() => setOpen(false)}
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
            <li>
              <Link to="/donor-signin" className="text-lg text-ink transition-colors hover:text-forest-700" onClick={() => setOpen(false)}>
                Donor sign in
              </Link>
            </li>
            <li>
              <Link to="/sponsor" className="mt-2 inline-flex items-center gap-3 rounded-full bg-forest-950 py-2 pl-5 pr-2 font-semibold text-white transition-colors hover:bg-forest-800" onClick={() => setOpen(false)}>
                Sponsor a Child
                <span className="grid h-9 w-9 place-items-center rounded-full bg-white text-forest-950">
                  <ArrowUpRight />
                </span>
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  )
}

const EXPLORE_LINKS = [
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/ability-program", label: "Ability Program" },
  { to: "/sponsor", label: "Sponsor a child" },
  { to: "/nominate", label: "Nominate a child" },
  { to: "/partner-schools", label: "Partner schools" },
  { to: "/womens-empowerment", label: "Women's Empowerment" },
  { to: "/events", label: "Events" },
  { to: "/donor-signin", label: "Donor sign in" },
] as const

const FOOTER_COPY = {
  home: {
    title: (
      <>
        Every life <span className="text-gold-500">deserves </span> a chance...
      </>
    ),
    body: "Redempta Development Foundation creates opportunities for under-educated and out of school children and young adults.",
  },
  inner: {
    title: (
      <>
        Every child <span className="text-gold-500">deserves </span> a classroom.
      </>
    ),
    body: "Redempta Development Foundation funds full scholarships that move children into quality private schools — fees, books, and materials covered.",
  },
} as const

function PublicFooter() {
  const { pathname } = useLocation()
  const copy = pathname === "/" || pathname === "/womens-empowerment" || pathname === "/donor-signin" || pathname === "/sponsor" || pathname === "/ability-program" ? FOOTER_COPY.home : FOOTER_COPY.inner

  return (
    <footer className={`${pathname === "/womens-empowerment" ? "womens-footer" : ""} ${pathname === "/sponsor" ? "sponsor-footer" : ""} ${pathname === "/ability-program" ? "ability-footer" : ""}`.trim() || undefined}>
      <div className="overflow-hidden bg-forest-700">
        <div className="public-container px-5 pb-4 pt-[30px] text-center lg:px-0 lg:pb-[64px] lg:pt-[100px]">
          <div className="mx-auto w-[358px] max-w-[calc(100vw-35px)] lg:w-[916px]">
            <h2 className="font-display text-[32px] font-bold leading-none text-white lg:text-[64px]">NEWSLETTER</h2>
            <p className="mt-[13px] text-sm leading-[17.5px] text-white lg:mt-[5px] lg:text-base lg:leading-normal">Redempta also has a newsletter!</p>
            <p className="mx-auto mt-1 w-[335px] max-w-full text-[13px] leading-[16.25px] text-white/85 lg:mt-[5px] lg:w-[916px] lg:text-base lg:leading-[23px]">
              On the agenda: news, tips and upcoming events from Redempta would be delivered to your inbox every week.
            </p>
          </div>
          <div className="mx-auto mt-[29px] w-[353px] max-w-[calc(100vw-40px)] lg:mt-[39px] lg:w-[456px]">
            <p className="text-base font-semibold text-white lg:text-base">Sign up for free!</p>
            <div className="mt-[15px] lg:mt-5">
              <NewsletterForm />
            </div>
            <p className="mx-auto mt-[17px] w-[255px] max-w-full text-xs leading-none text-white/70 lg:mt-2.5 lg:w-[340px] lg:text-sm lg:leading-5">By signing up, you agree to our privacy policy.</p>
            <Link to="/newsletter/unsubscribe" className="mt-3 inline-block text-xs text-white/70 underline transition-colors hover:text-white lg:text-sm">
              Unsubscribe from the newsletter
            </Link>
          </div>
        </div>
        <svg
          className="pointer-events-none block h-[42px] w-full lg:h-[56px]"
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M0 0 C245 98 456 112 720 68 C986 27 1196 -12 1440 0 V100 H0 Z" fill="var(--color-forest-950)" />
        </svg>
      </div>

      <div className="overflow-hidden bg-forest-950">
        <div className="public-container px-5 lg:px-0">
          <div className="mx-auto mt-[100px] grid gap-5 text-sm lg:mt-[170px] lg:w-[calc(100vw-100px)] lg:max-w-[1340px] lg:grid-cols-[1fr_auto] lg:gap-12 lg:text-lg">
            <div className="max-w-full">
              <h3 className="font-sans text-[22px] font-bold leading-7 text-white lg:text-[32px] lg:leading-snug">{copy.title}</h3>
              <p className="mt-2.5 text-sm leading-[18px] text-white lg:mt-4 lg:text-[18px] lg:leading-normal">{copy.body}</p>
            </div>

            <div className="grid grid-cols-2 gap-5 lg:hidden">
              <div>
                <p className="text-base font-medium text-white/75">EXPLORE</p>
                <ul className="mt-1.5 flex flex-col gap-1">
                  {EXPLORE_LINKS.map((link) => (
                    <li key={link.label}>
                      <Link to={link.to} className="text-sm font-medium leading-[21px] text-white transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-[25px]">
                <div>
                  <p className="text-base font-medium text-white/75">GET IN TOUCH</p>
                  <ul className="mt-1.5 flex flex-col gap-1 text-sm font-medium leading-[21px] text-white">
                    <li>
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="block break-words transition-colors hover:text-white">{SUPPORT_EMAIL}</a>
                    </li>
                    <li>Port Harcourt, Nigeria</li>
                    <li>
                      <Link to="/contact" className="inline-flex items-center gap-2 transition-colors hover:text-white">
                        Partner enquiry
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="sponsor-footer-follow">
                  <p className="text-base font-medium text-white/75">FOLLOW US ON</p>
                  <ul className="mt-1.5 flex flex-col gap-1 text-sm font-medium leading-[21px] text-white">
                    <li><a href="#" className="transition-colors hover:text-white">Facebook</a></li>
                    <li><a href="#" className="transition-colors hover:text-white">Twitter (X)</a></li>
                    <li><a href="#" className="transition-colors hover:text-white">Instagram</a></li>
                    <li><a href="#" className="transition-colors hover:text-white">Tiktok</a></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="hidden grid-cols-3 gap-10 lg:grid">
              <div>
                <p className="text-[18px] font-medium text-white/75">EXPLORE</p>
                <ul className="mt-3 flex flex-col gap-1.5 lg:mt-4 lg:gap-3">
                  {EXPLORE_LINKS.map((link) => (
                    <li key={link.label}>
                      <Link to={link.to} className="text-sm font-medium text-white transition-colors hover:text-white lg:text-lg">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[18px] font-medium text-white/75">GET IN TOUCH</p>
                <ul className="mt-3 flex flex-col gap-1.5 text-sm font-medium text-white lg:mt-4 lg:gap-3 lg:text-lg">
                  <li>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="block break-words transition-colors hover:text-white">{SUPPORT_EMAIL}</a>
                  </li>
                  <li>Port Harcourt, Nigeria</li>
                  <li>
                    <Link to="/contact" className="inline-flex items-center gap-2 transition-colors hover:text-white">
                      Partner enquiry
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className="text-[18px] font-medium text-white/75">FOLLOW US ON</p>
                <ul className="mt-3 flex flex-col gap-1.5 text-sm font-medium text-white lg:mt-4 lg:gap-3 lg:text-lg">
                  <li><a href="#" className="transition-colors hover:text-white">Facebook</a></li>
                  <li><a href="#" className="transition-colors hover:text-white">Twitter (X)</a></li>
                  <li><a href="#" className="transition-colors hover:text-white">Instagram</a></li>
                  <li><a href="#" className="transition-colors hover:text-white">Tiktok</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-[20px] flex max-w-[calc(100vw-40px)] justify-between text-white lg:mt-10 lg:w-[calc(100vw-100px)]">
            <p>{pathname === "/sponsor" ? "2026 Redempta Development Foundation." : "© 2026 Redempta Development Foundation."}</p>
            <p className={pathname === "/sponsor" ? "sponsor-footer-sdg" : "hidden lg:block"}>Aligned with UN SDGs 1 &middot; 4 &middot; 5 &middot; 10 &middot; 13</p>
          </div>

          <p className="mb-[30px] mx-auto whitespace-nowrap text-left font-watermark text-[52px] leading-none tracking-wide text-white lg:w-[calc(100vw-67px)] lg:max-w-[1373px] lg:text-center lg:text-[clamp(120px,12.5vw,180px)] lg:leading-[220px]">
            <span className="footer-wordmark hidden lg:block">REDEMPTA</span>
            <span className="lg:hidden">REDEMPTA</span>
          </p>
        </div>
      </div>
    </footer>
  )
}

export function PublicLayout() {
  const { pathname, search } = useLocation()

  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink">
      <PublicHeader />
      <main key={`${pathname}${search}`} className="animate-fade-in-up">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  )
}
